/* eslint max-len: 0 */
import { AppIntegrationsRepository, MeRepository, DevicesRepository, LicensesRepository } from 'common/repositories/index';
import { AppInstancesProcessor } from 'common/repositoryProcessors/index';
import Util from 'common/services/util/-RFC.util';
/**
 * Device Processor - devices grouped by userId and device state (active/pending/hidden)
 */

let processedDevices = [];
const observableProcessedDevices = new Rx.BehaviorSubject(processedDevices);

/**
* @ngdoc service
* @name processors.DevicesProcessor
*
* @description
* This class is used to process devices.
*/
const DevicesProcessor = {

    /**
     * Replace all devices with received data
     * @param {Array} data
     */
    onNext(data) {

        processedDevices = [];
        data.forEach(device => this._addDevice(device));
        observableProcessedDevices.onNext(processedDevices);

    },

    /**
     * Add device based on its userId and state
     * @param device
     */
    _addDevice(device) {

        const userId = device.relationships.user.data.id;
        const { state } = device.attributes;

        processedDevices[userId] = processedDevices[userId] || {};
        processedDevices[userId][state] = processedDevices[userId][state] || [];
        processedDevices[userId][state].push(device);

    },

    /**
     * @ngdoc function
     * @name processors.DevicesProcessor#getAll
     * @methodOf processors.DevicesProcessor
     * @function
     *
     * @description
     * This function can be used to return all processed devices.
     *
     * @returns {Array} all processed devices
     */
    getAll() {

        return processedDevices;

    },

    /**
     * @ngdoc function
     * @name processors.DevicesProcessor#getByUserId
     * @methodOf processors.DevicesProcessor
     * @function
     *
     * @description
     * This function can be used to return devices of a specified user.
     *
     * @returns {Array} all processed devices
     */
    getByUserId(userId) {

        return processedDevices[userId];

    },

    /**
     * @ngdoc function
     * @name processors.DevicesProcessor#hasDisplayedDevices
     * @methodOf processors.DevicesProcessor
     * @function
     *
     * @description
     * This function can be used to check for displayed devices.
     * @returns {bool} true or false.
     */
    hasDisplayedDevices() {

        const index = DevicesRepository
            .getAll()
            .findIndex(_device => DevicesRepository.isVisible(_device));

        return index >= 0;

    },


    /**
     * @ngdoc function
     * @name processors.DevicesProcessor#hasAtLeastOneLaptopOrPc
     * @methodOf processors.DevicesProcessor
     * @function
     *
     * @description
     * This function can be used to check if the user has at least one visible PC or Laptop.
     * @returns {Object} Device object
     * @returns {Undefined} if devices was not found
     */
    hasAtLeastOneLaptopOrPc() {

        const visibleDevices = DevicesRepository.getVisibleDevices();

        return visibleDevices.find(device => (
            device.attributes.type === 'pc' ||
            device.attributes.type === 'laptop'));

    },

    /**
     * @ngdoc function
     * @name processors.DevicesProcessor#hasAtLeastOnePhoneOrTablet
     * @methodOf processors.DevicesProcessor
     * @function
     *
     * @description
     * This function can be used to check if the user has at least one visible Phone or Tablet.
     * @returns {Object} Device object
     * @returns {Undefined} if devices was not found
     */
    hasAtLeastOnePhoneOrTablet() {

        const visibleDevices = DevicesRepository.getVisibleDevices();

        return visibleDevices.find(device => (
            device.attributes.type === 'phone' ||
            device.attributes.type === 'tablet'));

    },

    /**
     * @ngdoc function
     * @name processors.DevicesProcessor#getDevicesByApp
     * @methodOf processors.DevicesProcessor
     * @function
     *
     * @description
     * This function returns the devices which have the specified app installed.
     *
     * @param {Object} app the object of the specified app.
     *
     * @returns {Array} devices which have the app installed.
     */
    getDevicesByApp(app) {

        let instances = [];
        const devices = [];

        if (!app) {

            return instances;

        }

        instances = AppInstancesProcessor.getInstancesByPrototype(
            MeRepository.getMyId(),
            app.attributes.prototype,
        );

        _.values(instances).forEach((instance) => {

            if (Util.deepFind(instance, 'relationships.device')) {

                const device = DevicesRepository.getById(instance.relationships.device.data.id);
                if (!_.contains(devices, device) &&
                    DevicesRepository.isVisible(device)) {

                    devices.push(device);

                }

            }

        });

        return devices;

    },
    /**
     * @ngdoc function
     * @name processors.DevicesProcessor#getDevicesByApp
     * @methodOf processors.DevicesProcessor
     * @function
     *
     * @description
     * This function returns the devices which have the specified app installed.
     *
     * @param {string} licenseId.
     *
     * @returns {Array} devices which have the app installed.
     */
    getDevicesByLicenseId(licenseId) {

        let instances = [];
        const devices = [];

        instances = AppInstancesProcessor.getInstancesByLicenseId(
            MeRepository.getMyId(),
            licenseId,
        );

        instances.forEach((instance) => {

            if (instance.attributes.state === 'active') {

                const device = DevicesRepository.getById(instance.relationships.device.data.id);

                if (device) {

                    const indexOfdevice = devices.findIndex(item => item.id ===
                        instance.relationships.device.data.id);

                    if (indexOfdevice === -1) {

                        if (device.attributes.hidden === false && device.attributes.state !== 'pending') {

                            devices.push(device);

                        }

                    }

                }

            }

        });
        return devices;

    },

    /**
     * @ngdoc function
     * @name processors.DevicesProcessor#getFirstDeviceOf
     * @methodOf processors.DevicesProcessor
     * @function
     *
     * @description
     * This function calculates the selected device which has the specified app installed.
     * If the app is not installed on any device, return 'new'.
     *
     * @param {Object} app the specified app.
     *
     * @returns {Object} the selected device for that app.
     */
    getFirstDeviceOf(app) {

        const devices = this.getDevicesByApp(app);
        return (devices.length) ? devices[0] : null;

    },

    /**
     * @ngdoc function
     * @name processors.DevicesProcessor#getFirstMobileDeviceOf
     * @methodOf processors.DevicesProcessor
     * @function
     *
     * @description
     * This function calculates the selected mobile device which has the specified app installed.
     * If the app is not installed on any mobile device, return 'new'.
     *
     * @param {Object} app the specified app.
     *
     * @returns {Object} the selected mobile device for that app.
     */
    getFirstMobileDeviceOf(app) {

        const devices = this.getDevicesByApp(app);
        let mobileDevices;

        if (devices) {

            mobileDevices = devices.filter(device =>

                (device.attributes.type === 'phone' || device.attributes.type === 'tablet'));

        }
        return (mobileDevices.length) ? mobileDevices[0] : null;

    },

    /**
     * @ngdoc function
     * @name processors.DevicesProcessor#getLocation
     * @methodOf processors.DevicesProcessor
     * @function
     *
     * @description
     * Returns the location of a device.
     *
     * @param {int} userId the user.
     * @param {String} prototype the prototype of the family locator service.
     * @param {Object} device the device object.
     *
     * @returns {Object} the location of the device.
     */
    getLocation(userId, prototype, device) {

        const instances = AppInstancesProcessor.getInstancesByPrototypeAndDevice(
            userId,
            prototype,
            device,
        );

        if (!instances.length) {

            return undefined;

        }

        const locationInstance = instances
            .find(instance => AppInstancesProcessor.hasLocationInfo(instance));

        return Util.deepFind(locationInstance, 'attributes.status.LOCATE');

    },

    /**
     * @ngdoc function
     * @name processors.DevicesProcessor#hasAvInstalled
     * @methodOf processors.DevicesProcessor
     * @function
     *
     * @description
     * Checks if the device has antivirus installed.
     *
     * @param {String} userId the user id.
     * @param {String} deviceId the device id.
     * @returns {Boolean} True if the device has av installed.
     */
    hasAvInstalled(userId, deviceId) {

        const deviceInstances = AppInstancesProcessor.getInstancesByDevice(userId, deviceId);

        const avInstance = _.values(deviceInstances).find((instance) => {

            if (instance.attributes.state !== 'active') {

                return false;

            }

            if (instance.customAppInfo.prototype === 'antivirus') {

                return true;

            }

            const appId = Util.deepFind(instance, 'relationships.app.data.id');
            // if antivirus is installed on the phone
            if (appId === CWCONFIG.defaultDeployedAppId.android ||
                appId === CWCONFIG.defaultDeployedAppId.ios ||
                appId === 'aasc0' ||
                appId === 'mspa0' ||
                appId === 'mspi0') {

                return true;

            }

            return false;

        });

        return !!avInstance;

    },

    /**
     * @ngdoc function
     * @name processors.DevicesProcessor#getDeployOSfor
     * @methodOf processors.DevicesProcessor
     * @function
     *
     * @description
     * We use this function to find a suitable deployment os for a product and a device we're on
     * if one exists
     * @returns {String} a valid os on which to deploy a product.
     */
    getDeployOSfor(appId, currentDeviceOS) {

        let os;

        if (currentDeviceOS === 'mac_os_x') {

            currentDeviceOS = 'osx';

        }

        const osList = AppIntegrationsRepository.getSupportedOs(appId);

        if (osList && osList.length > 0) {

            os = osList.filter(osName => currentDeviceOS.indexOf(osName) > -1);
            if (os.length === 0) {

                os = null;

            } else {

                [os] = os; // we want to return a string;

            }

        }

        return os;

    },

    /**
     * @ngdoc function
     * @name processors.DevicesProcessor#getCompatibleDevices
     * @methodOf processors.DevicesProcessor
     * @function
     *
     * @description
     * This function returns the list of pc and laptop
     * existing devices on which an integration may be installed.
     *
     * @param {string} appIntegrationId id of integration
     * @param {string} prototype of integration
     *
     * @returns {Array} Array with the devices on which the integration may be installed.
     */
    getCompatibleDevices(appIntegrationId, prototype) {

        const compatibleDevices = [];
        const userId = MeRepository.getMyId();
        const displayedDevices = DevicesRepository.getByState(['online', 'offline'], false);

        if (!displayedDevices) {

            return compatibleDevices;

        }

        displayedDevices.forEach((device) => {

            if (!DevicesRepository.isMobile(device.id) &&
                DevicesRepository.isDeviceCompatibleWithIntegration(
                    device.id,
                    appIntegrationId,
                ) &&
                !AppInstancesProcessor.getByAppPrototypeAndDevice(
                    userId,
                    prototype,
                    device.id,
                )) {

                compatibleDevices.push(device);

            }

        });

        return compatibleDevices;

    },


    /**
     * @ngdoc function
     * @name processors.DevicesProcessor#detectLicenseType
     * @methodOf processors.DevicesProcessor
     * @function
     *
     * @description
     * This function returns a list of devices and info about the license installed on them
     *
     * @param {Array} devices
     *
     * @returns {Array} Array with devices with the updated info about licenses
     */

    detectLicenseType(devices) {

        const expireNotificationDays = CWCONFIG.subscriptionExpirationReminderDays || 15;
        const currentDateInMiliseconds = new Date().getTime();

        if (devices && devices.length) {

            devices.forEach((device) => {

                device.attributes.licenses = [];
                device.attributes.showLicenseWarning = false;

                const deviceAppInstances = AppInstancesProcessor
                    .getInstancesByDevice(device.relationships.user.data.id, device.id);

                if (deviceAppInstances) {

                    deviceAppInstances.forEach((instance) => {

                        if (instance && instance.attributes.state === 'active') {

                            device.attributes.hasInstances = true;

                            const instanceLicense = LicensesRepository
                                .getLicenseById(instance.relationships.license.data.id);

                            if (instanceLicense) {

                                const integrationDetails = AppIntegrationsRepository
                                    .getById(instanceLicense.relationships.app.data.id);

                                if (integrationDetails) {

                                    // make sure not to store doubles
                                    const { oeName } = integrationDetails.attributes.resource;

                                    if (device.attributes.licenses.indexOf(oeName) === -1) {

                                        device.attributes.licenses.push(oeName);

                                    }

                                }

                                if (CWCONFIG.devices.bundles.indexOf(instanceLicense.relationships.app.data.id) > -1) {

                                    device.attributes.hasBundle = true;

                                }

                                // check if the license expires to display the warning tooltip
                                const expirationDate = instanceLicense.attributes.expiration_date;
                                const appId = instanceLicense.relationships.app.data.id || '';

                                if (expirationDate) {

                                    const subscriptionExpirationDateInMs = moment(expirationDate).valueOf();
                                    const remainingDaysUntilExpiration = Math.ceil(moment.duration(subscriptionExpirationDateInMs - currentDateInMiliseconds).asDays());

                                    if (remainingDaysUntilExpiration <= expireNotificationDays &&
                                        instanceLicense.attributes.subscription === 0 &&
                                        !Util.isNonrenewableApp(appId)) {

                                        device.attributes.showLicenseWarning = true;

                                    }

                                }

                            } else {

                                // Check if the license is not compatible with the device os
                                const licensesByOs = LicensesRepository.getLicensesByOs(device.attributes.os);

                                if (licensesByOs && licensesByOs.length === 0) {

                                    device.attributes.hasIncompatibleLicense = true;

                                }

                            }

                        }

                    });

                }

            });

        }

        return devices;

    },

    /**
     * @ngdoc function
     * @name processors.DevicesProcessor#detectFreeProducts
     * @methodOf processors.DevicesProcessor
     * @function
     *
     * @description
     * This function returns a list of devices and info about the free products installed on them
     *
     * @param {Array} devices
     *
     * @returns {Array} Array with devices with the updated info about free products installed
     */

    detectFreeProducts(devices) {

        if (devices && devices.length) {

            devices.forEach((device) => {

                device.attributes.freeProductsInstalled = [];

                const deviceAppInstances = AppInstancesProcessor
                    .getInstancesByDevice(device.relationships.user.data.id, device.id);

                if (deviceAppInstances) {

                    deviceAppInstances.forEach((instance) => {

                        if (instance && instance.attributes.state === 'active' &&
                            instance.customAppInfo.visible && instance.customAppInfo.visible !== 0) {

                            if (instance.relationships.license.data.id.includes('-0-') ||
                                instance.relationships.license.data.id.slice(-2) === '-0') {

                                device.attributes.freeProductsInstalled.push(instance.customAppInfo.name);

                            }

                        }

                    });

                }

            });

        }

        return devices;

    },

    /**
    * @ngdoc function
    * @name processors.DevicesProcessor#getObservable
    * @methodOf processors.DevicesProcessor
    * @function
    *
    * @description
    * This function can be used to get an observable object for processed devices.
    * @returns {Object} an observable object for processed devices.
    */
    getObservable() {

        return observableProcessedDevices;

    },

};

const subjectDevicesRepository = DevicesRepository.getObservable();

subjectDevicesRepository.subscribe(
    data => DevicesProcessor.onNext(data),
    () => { },
    () => { },
);

export default DevicesProcessor;



// WEBPACK FOOTER //
// ./scripts/common/repositoryProcessors/-RFC.devicesProcessor.js